import {
	Button,
	Dimmer,
	Dropdown,
	type DropdownItemProps,
	type DropdownProps,
	Header,
	Loader,
	Modal,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import { isEmpty, isNil } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import type ApercuNotePresentation from '../../../../interfaces/simulation/apercuNotePresentation';
import type LigneNotePresentation from '../../../../interfaces/simulation/ligneNotePresentation';
import type NotePresentation from '../../../../interfaces/simulation/notePresentation';
import type OutilModalProps from '../../../../interfaces/simulation/outilModalProps';
import { getAllNotePresentation } from '../../../../services/simulation/notePresentation';
import { ordonnerLignes } from '../../../../utils/simulation/notePresentation';
import genererDocumentNotePresentation from '../../../../utils/simulation/notePresentationDocument';
import UpdateModal from '../../../parametres/NotePresentation/Block/Modal/UpdateModal';
import NotePresentationBloc from './NotePresentationBloc';
import './NotePresentationModal.css';

interface NotePresentationModalProps extends OutilModalProps {
	onApercu: (apercu: ApercuNotePresentation) => void;
}

const NotePresentationModal = ({
	onApercu,
	onClose,
	open,
	simulation,
}: NotePresentationModalProps) => {
	const [ligneSelected, setLigneSelected] =
		React.useState<LigneNotePresentation>(null);
	const [loading, setLoading] = React.useState<boolean>(true);
	const [identifiantTrame, setIdentifiantTrame] = React.useState<number>(null);
	const [trames, setTrames] = React.useState<NotePresentation[]>([]);

	const trameSelected = trames.find(
		(trame) => trame.identifiant === identifiantTrame,
	);

	const options: DropdownItemProps[] = trames.map((trame) => ({
		'data-testid': `trame${trame.identifiant}`,
		key: trame.identifiant,
		text: trame.libelle,
		value: trame.identifiant,
	}));

	// Les trames disponibles dépendent de l'étape budgétaire de la simulation
	const fetchTrames = async () => {
		try {
			setLoading(true);
			setLigneSelected(null);

			const { data } = await getAllNotePresentation(
				new URLSearchParams({
					all: 'true',
					...(simulation?.budget?.type
						? { etapeBudgetaire: simulation.budget.type }
						: {}),
					withLignes: 'true',
				}),
			);

			// Une trame vidée de ses blocs n'a rien à imprimer
			const tramesDisponibles = data.filter((trame) => !isEmpty(trame.lignes));

			setTrames(tramesDisponibles);
			setIdentifiantTrame(tramesDisponibles[0]?.identifiant ?? null);
		} catch (e) {
			toast.error(
				getErrorMessage(e, 'Erreur lors du chargement des trames de note'),
			);
		} finally {
			setLoading(false);
		}
	};

	const handleChangeTrame = (
		_e: React.SyntheticEvent<HTMLElement>,
		{ value }: DropdownProps,
	) => setIdentifiantTrame(value as number);

	const handleCloseUpdate = () => setLigneSelected(null);

	// Les modifications ne sont pas mémorisées : elles n'alimentent que le
	// document généré depuis cette modale
	const handleSaveLigne = (ligneModifiee: LigneNotePresentation) => {
		setTrames((prevTrames) =>
			prevTrames.map((trame) =>
				trame.identifiant === identifiantTrame
					? {
							...trame,
							lignes: trame.lignes.map((ligne) =>
								ligne.identifiant === ligneModifiee.identifiant
									? ligneModifiee
									: ligne,
							),
						}
					: trame,
			),
		);
		setLigneSelected(null);
	};

	// La modale laisse la place au viewer, qui reste dans l'application pour
	// conserver la navigation et le style
	const handleGenererApercu = () => {
		const documentNote = genererDocumentNotePresentation(
			trameSelected,
			simulation,
		);

		if (!documentNote) {
			toast.error("La trame sélectionnée n'a aucun contenu à imprimer");
			return;
		}

		onApercu({ documentNote, libelle: trameSelected.libelle });
		onClose();
	};

	const renderContent = () => {
		if (loading) {
			return (
				<Dimmer active inverted>
					<Loader size="massive" />
				</Dimmer>
			);
		}

		if (isEmpty(trames)) {
			return (
				<Header
					content="Aucune trame de note de présentation disponible"
					size="small"
					textAlign="center"
				/>
			);
		}

		return (
			<>
				<Dropdown
					data-testid="selectionTrame"
					fluid
					onChange={handleChangeTrame}
					options={options}
					placeholder="Sélectionnez une trame de note"
					selection
					value={identifiantTrame}
				/>
				<div className="notePresentationBlocs">
					{ordonnerLignes(trameSelected?.lignes).map((ligne) => (
						<NotePresentationBloc
							key={ligne.identifiant}
							ligne={ligne}
							onUpdate={setLigneSelected}
						/>
					))}
				</div>
			</>
		);
	};

	return (
		<>
			<Modal closeIcon onClose={onClose} onMount={fetchTrames} open={open}>
				<Modal.Header content="Rédiger la note de présentation" />
				<Modal.Content className="notePresentationModalContent">
					{renderContent()}
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Générer le PDF"
						data-testid="genererPdfButton"
						disabled={isNil(trameSelected)}
						onClick={handleGenererApercu}
						positive
					/>
				</Modal.Actions>
			</Modal>
			<UpdateModal
				ligne={ligneSelected}
				onClose={handleCloseUpdate}
				onSave={handleSaveLigne}
				open={!isNil(ligneSelected)}
			/>
		</>
	);
};

export default NotePresentationModal;
